import React from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  Typography,
  TypographyProps,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
  paragraphTypoProps,
  sectionTwoShadingTypoProps,
  sectionTwoTypoProps,
} from '../Home';
import AIMarkingSection from '../components/AIMarkingSection';

const aiTypoSubProps: TypographyProps = {
  component: 'span',
  fontSize: '52px',
  fontWeight: 800,
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

interface DetailedQuizModalModalProps {
  open: boolean;
  onClose: () => void;
}

const DetailedQuizModal: React.FC<DetailedQuizModalModalProps> = ({
  open,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          padding: '7px',
          borderRadius: '30px',
          width: '1000px',
          maxHeight: '700px',
        },
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogContent
        sx={{
          background: `url(${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/detailed-quiz-graphic.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          borderRadius: '30px',
        }}
      >
        <Typography sx={{ ...sectionTwoTypoProps, marginTop: '15px' }}>
          <Typography {...aiTypoSubProps}>AI-Powered </Typography>
          Detailed Quiz{' '}
          <Typography sx={sectionTwoShadingTypoProps}>Report</Typography>
        </Typography>
        <Typography sx={paragraphTypoProps} marginBottom="20px">
          Get a comprehensive quiz breakdown with AI Marking Scheme, Analysis, &
          Examiner Recommendations. Improve with personalized feedback and clear
          next steps
        </Typography>
        <img
          src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/detailed-quiz-background.png`}
          alt="detail-banner"
          style={{
            width: '100%',
            height: 'auto',
            WebkitMaskImage:
              'linear-gradient(to bottom, black 70%, transparent 100%)',
            maskImage:
              'linear-gradient(to bottom, black 70%, transparent 100%)',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
            Scroll down
          </Typography>
          <img
            src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/scroll-black.gif`}
            alt="scroll-graphic"
            style={{ width: '30px', height: 'auto', marginTop: '5px' }}
          />
        </Box>

        <Grid container spacing={6} maxWidth={780} mx="auto" mt="5px">
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-start">
              <Box display="flex" gap={2} maxWidth={420}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/hour-glass.png`}
                  alt="scroll-graphic"
                  style={{
                    width: '180px',
                    height: '180px',
                    marginTop: '-65px',
                    marginLeft: '-45px',
                    marginRight: '-75px',
                  }}
                />
                <Box>
                  <Typography fontSize="18px" fontWeight={600}>
                    Performance Snapshot
                  </Typography>
                  <Typography fontSize="16px">
                    All key stats in one place—score, speed, accuracy, and last
                    attempt for smarter tracking.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Box display="flex" gap={2} maxWidth={420}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/trophy.png`}
                  alt="strength"
                  style={{
                    width: '180px',
                    height: '160px',
                    marginRight: '-65px',
                    marginLeft: '-45px',
                    marginTop: '-50px',
                  }}
                />
                <Box>
                  <Typography fontSize="18px" fontWeight={600}>
                    Areas of Strengths & Improvements
                  </Typography>
                  <Typography fontSize="16px">
                    Get a breakdown of what you&apos;re great at and what still
                    needs a little push.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-start">
              <Box display="flex" gap={2} maxWidth={420}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/robot.png`}
                  alt="strength"
                  style={{
                    width: '180px',
                    height: '160px',
                    marginRight: '-65px',
                    marginLeft: '-45px',
                    marginTop: '-50px',
                  }}
                />
                <Box>
                  <Typography fontSize="18px" fontWeight={600}>
                    AI Examiner Insights
                  </Typography>
                  <Typography fontSize="16px">
                    Get smart suggestions and see where you stand compared to
                    other learners.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Box display="flex" gap={2} maxWidth={420}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/landingPage/newLandingPage/doc.png`}
                  alt="strength"
                  style={{
                    width: '180px',
                    height: '160px',
                    marginRight: '-45px',
                    marginLeft: '-45px',
                    marginTop: '-65px',
                  }}
                />
                <Box>
                  <Typography fontSize="18px" fontWeight={600}>
                    Detailed Question Review
                  </Typography>
                  <Typography fontSize="16px">
                    Review every question with AI-powered explanations and
                    structured feedback.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="25px">
          <AIMarkingSection />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DetailedQuizModal;
